import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Field, FormError } from '../../components/Field';
import { SkyPage } from '../../components/SkyPage';
import { da } from '../../i18n/da';
import { login } from './auth-api';
import { useAuth } from './auth-context';
import { safeNextPath } from './safe-next';
import styles from './AuthPage.module.css';

export function LoginPage() {
  const { state, signedIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (state.status === 'ready' && state.user) {
    return <Navigate to={next} replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const result = await login({ username, password });
    setBusy(false);
    if (!result.ok) {
      setError(da.authErrors[result.error]);
      return;
    }
    signedIn(result.data.user);
    void navigate(next, { replace: true });
  };

  return (
    <SkyPage>
      <Card className={styles.card} aria-labelledby="login-title">
        <h2 id="login-title" className={styles.title}>
          {da.auth.loginTitle}
        </h2>
        <form className={styles.form} onSubmit={(event) => void handleSubmit(event)}>
          <Field
            label={da.auth.usernameLabel}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
          />
          <Field
            label={da.auth.passwordLabel}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
          <FormError message={error} />
          <Button type="submit" disabled={busy || !username || !password}>
            {da.auth.loginButton}
          </Button>
        </form>
        <p className={styles.footer}>
          {da.auth.noAccount} <Link to="/register">{da.auth.goToRegister}</Link>
        </p>
        <p className={styles.footer}>
          <Link to="/parents">{da.parents.link}</Link>
        </p>
      </Card>
    </SkyPage>
  );
}
